<template>
  <div class="row description-page" :class="$config.project">
    <div class="first-column col col-8">
      <section>
        <h2 class="title">
          Case Description
        </h2>
        <div class="description" v-html="project.description" />
      </section>
      <section v-if="project.requirements">
        <h2 class="title">
          Project requirements
        </h2>
        <div class="description" v-html="project.requirements" />
      </section>
      <section class="third">
        <h2 class="title">
          Project files
        </h2>
        <div class="description" v-html="project.fileDescription" />
        <nuxt-link v-if="$config.project === 'site'" to="/signup/" class="btn btn-primary btn-lg">
          Start project
        </nuxt-link>
        <button v-else-if="!project.locked && project.files > 0" class="btn-primary btn" @click="$emit('download', 'project-files')">
          Project files <IconFile />
        </button>
        <nuxt-link v-else-if="project.files > 0" to="/pricing/" class="btn-primary btn">
          Upgrade to unlock
        </nuxt-link>
      </section>
    </div>
    <div class="second-column col col-4">
      <section class="project-content">
        <IconProjectContent />
        <span class="h5">
          Project content
        </span>
        <ul>
          <li>{{ project.files }} Project file{{ project.files > 1 ? 's' : '' }}</li>
          <li>Guided and unguided instructions <span v-if="project.rewards" class="reward"> Up to {{ project.rewards.instructions.unguided.xp }}  XP</span></li>
          <li v-for="(step, i) in project.steps" :key="i">
            Part {{ i + 1 }}: {{ step.name }}
          </li>
          <li>Quiz <span v-if="project.rewards" class="reward"> Up to {{ project.rewards.maxQuizReward.xp }} XP</span></li>
        </ul>
      </section>
      <section class="featured">
        <IconFeatured />
        <span class="h5">Featured tools</span>
        <div class="technologies">
          <span v-for="(technology, id) in project.technologies" :key="id">
            <components :is="require(`../assets/svg/tech-responsive-${technology}.svg?vue-component`)" />
          </span>
        </div>
      </section>
      <section class="topics-covered">
        <IconTopics />
        <span class="h5">Topics covered</span>
        <div class="topics">
          <span v-for="(topic, id) in project.topics" :key="id">
            {{ topic }}
          </span>
        </div>
      </section>
      <section class="related">
        <IconRelated />
        <span class="h5">
          {{ relatedType }}
        </span>
        <p class="subtitle">
          To complete this project you need expertise on the following topic(s):
        </p>
        <ul>
          <li v-for="(related, idx) in project.related" :key="idx">
            <nuxt-link
              :to="`/${type}${related.slug}/`"
            >
              {{ related.name }}
            </nuxt-link>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script>
import IconFile from '../assets/svg/icon-project-files.svg?vue-component';
import IconProjectContent from '../assets/svg/icon-project-content.svg?vue-component';
import IconFeatured from '../assets/svg/icon-project-featured.svg?vue-component';
import IconTopics from '../assets/svg/icon-project-topics.svg?vue-component';
import IconRelated from '../assets/svg/icon-project-related-courses.svg?vue-component';

export default {
  components: {
    IconFile, IconProjectContent, IconFeatured, IconTopics, IconRelated,
  },
  props: {
    project: { type: Object, required: true },
  },
  computed: {
    type() {
      if (this.project.subtype === 'Career Track') {
        return 'career-tracks/';
      }
      return `courses/${this.$config.project === 'lms' ? 'preview/' : ''}`;
    },
    relatedType() {
      if (this.project.subtype === 'Career Track') {
        return 'Related tracks';
      }
      return 'Related courses';
    },
  },
  async mounted() {
    await this.$pollValue(() => typeof window.MathJax !== 'undefined');
    if (window.MathJax) {
      window.MathJax.Hub.Config({
        tex2jax: {
          inlineMath: [['$', '$'], ['\\(', '\\)']],
          processEscapes: true,
        },
        'HTML-CSS': {
          linebreaks: { automatic: true, width: 'container' },
        },
      });
      window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
    }
  },
};
</script>

<style lang="scss">
.description-page {
  padding-bottom: var(--spacing-110);

  &.site {
    padding-bottom: 130px;

    .description {
      line-height: 1.625;
    }

    .first-column {
      section .btn-primary {
        width: 225px;
        height: 58px;
      }
    }

    .second-column {
      .project-content ul li .reward {
        display: none;
      }

      .topics-covered .topics span {
        font-size: 14px;
      }

      .related ul li a {
        line-height: 1.38;
      }
    }
  }

  .first-column {
    section {
      padding: var(--spacing-32);
      box-shadow: 0 16px 30px rgba(16, 20, 28, 0.2);
      border-radius: 20px;
      background: var(--main-light);

      &:not(:last-child) {
        margin-bottom: var(--spacing-20);
      }

      .title {
        font-size: 24px;
        font-weight: 600;
        line-height: 125%;
        margin-bottom: var(--spacing-32);
      }

      .title + p {
        margin: 0;
      }

      .btn-primary {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 9px 0;
        width: 170px;
        height: 40px;
        margin-top: var(--spacing-20);

        svg {
          margin-left: var(--spacing-8);

          path[fill] {
            fill: var(--main-light);
          }
        }
      }

      code:not([class*="language-"]) {
        background: #E8E8E8;
        padding: 1px 3px;
        border-radius: 3px;
        color: var(--main-dark);
      }

      &.third p:last-child {
        margin-bottom: 0;
      }
    }
  }

  .second-column {
    .h5 {
      font-weight: 600;
    }

    svg {
      width: 20px;
      vertical-align: text-bottom;
      margin-right: var(--spacing-12);
    }

    .project-content,
    .featured,
    .topics-covered {
      border-bottom: 1px solid var(--main-light-shade-3);
      padding-bottom: var(--spacing-44);
      margin-bottom: var(--spacing-44);
    }

    .project-content {
      ul {
        padding-left: var(--spacing-10);
        margin-top: var(--spacing-20);
        margin-bottom: 0;

        li {
          line-height: 26px;
          margin-bottom: var(--spacing-16);
          list-style-position: outside;
          margin-left: 16px;

          &:last-of-type {
            margin-bottom: 0;
          }

          .reward {
            color: var(--main-purple);
            font-size: 14px;
            font-weight: 600;
            border: 1px solid var(--main-purple);
            border-radius: 5px;
            padding: 0 var(--spacing-4);
            display: inline-block;
          }
        }
      }
    }

    .featured {
      .technologies {
        margin-top: var(--spacing-20);
        display: flex;

        span {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: inline-flex;
          justify-content: center;
          background: var(--accent);
          vertical-align: bottom;
          margin-right: var(--spacing-16);

          &:last-of-type {
            margin-right: 0;
          }

          svg {
            margin-right: 0;
          }
        }
      }
    }

    .topics-covered {
      .topics {
        margin-top: var(--spacing-20);

        span {
          font-size: 12px;
          line-height: 20px;
          background: var(--main-light-shade-3);
          border-radius: 100px;
          padding: var(--spacing-6) var(--spacing-12);
          margin-right: var(--spacing-8);
          margin-bottom: var(--spacing-12);
          display: inline-block;
          text-transform: capitalize;
        }
      }
    }

    .related {
      p {
        color: var(--main-dark-light-3);
        margin-top: var(--spacing-20);
        margin-bottom: var(--spacing-22);
        font-size: 16px;
        line-height: 23px;
      }

      ul {
        padding-left: var(--spacing-28);

        li {
          color: var(--accent-shade-1);
          list-style: none;
          margin-bottom: var(--spacing-12);
          font-weight: 600;
          position: relative;

          &:before {
            content: "\2022";
            color: var(--accent);
            position: absolute;
            left: -16px;
          }

          a {
            color: var(--accent);
          }

          &:last-of-type {
            margin-bottom: 0;
          }
        }
      }
    }
  }
}

@include bp($bp-laptop) {
  .description-page {
    .first-column {
      section {
        .title {
          font-size: 21px;
          line-height: 119%;
        }
      }
    }

    .second-column {
      .h5 {
        font-size: 18px;
      }
    }
  }
}

@include bp($bp-phone) {
  .description-page {
    padding-bottom: var(--spacing-80);

    .first-column,
    .second-column {
      section {
        margin-left: -24px;
        margin-right: -24px;
        border-radius: 0;
        background: var(--main-light);
        padding: var(--spacing-44) var(--spacing-24);
        box-shadow: 0 16px 30px rgba(0, 12, 31, 0.25);

        button {
          width: 100%;
          height: 52px;
        }
      }
    }

    .second-column {
      margin-top: var(--spacing-20);

      .project-content,
      .featured,
      .topics-covered {
        border-bottom: unset;
        margin-bottom: var(--spacing-20);
      }
    }
  }
}

@include bp($bp-tablet) {
  .description-page {
    &.row {
      margin-left: -12px;
      margin-right: -12px;
    }

    .second-column {
      margin-top: var(--spacing-20);
    }
  }
}

.fa {
  .description-page {
    .second-column {
      svg {
        path[stroke],
        rect[stroke] {
          stroke: var(--accent);
        }

        rect[fill]:nth-child(3) {
          fill: var(--accent);
        }
      }

      .related ul li {
        color: var(--accent);
      }
    }
  }
}
</style>
